import { AddRounded, DeleteRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEntityFormContext, useEntityFormFieldArray, useInternationalization } from '../../hooks';
import { EmailAddressEntity } from '../../models';
import { palette } from '../../styles/palette';
import { Styles } from '../../types';
import { ControlledInput, ControlledTranslatedInput } from '../Form';
import { MAX_LONG_TEXT_LENGTH, MAX_SHORT_TEXT_LENGTH } from '../../constants';

const style: Styles = {
  tableCell: {
    px: 1.25,
    py: 0.5,
    verticalAlign: 'top',
  },
  table: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableRow: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${palette.grey[300]}`,
    },
  },
  tableButton: {
    mt: 3.5,
  },
  buttonBox: {
    mt: 1,
  },
  tableCellSingleLanguage: {
    pt: '35px !important',
  },
};

interface EmailAddressListFormProps {
  disableTranslation?: boolean;
}

export const EmailAddressListForm = ({ disableTranslation }: EmailAddressListFormProps) => {
  const { t } = useInternationalization();
  const { control, readOnly } = useEntityFormContext();
  const emailAddresses = useEntityFormFieldArray(control, 'emailAddresses');

  return (
    <>
      <TableContainer>
        <Table sx={style.table}>
          <TableHead>
            <TableRow>
              <TableCell width="55%">{t('common:contact.email.address')}</TableCell>
              <TableCell width="45%">{t('common:contact.email.description')}</TableCell>
              {!readOnly && <TableCell variant="head"></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {emailAddresses.fields.map(
              (item: Record<string, string>, index) =>
                !item.isDeleted && (
                  <TableRow key={item.id} sx={style.tableRow}>
                    <TableCell
                      sx={[style.tableCell, disableTranslation || readOnly ? null : style.tableCellSingleLanguage]}
                    >
                      <ControlledInput
                        hideLabel
                        label={t('common:contact.email.address')}
                        name={`emailAddresses.${index}.email`}
                        linkType="email"
                        control={control}
                        maxLength={MAX_SHORT_TEXT_LENGTH}
                      />
                    </TableCell>
                    <TableCell sx={style.tableCell}>
                      {disableTranslation ? (
                        <ControlledInput
                          hideLabel
                          label={t('common:contact.email.description')}
                          name={`emailAddresses.${index}.description`}
                          maxLength={MAX_LONG_TEXT_LENGTH}
                          control={control}
                        />
                      ) : (
                        <ControlledTranslatedInput
                          hideLabel
                          label={t('common:contact.email.description')}
                          name={`emailAddresses.${index}.description`}
                          maxLength={MAX_LONG_TEXT_LENGTH}
                          control={control}
                        />
                      )}
                    </TableCell>
                    {!readOnly && (
                      <TableCell sx={style.tableCell}>
                        <IconButton
                          onClick={() => emailAddresses.remove(index)}
                          sx={disableTranslation ? undefined : style.tableButton}
                        >
                          <DeleteRounded />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ),
            )}
            {!emailAddresses.fields.length && (
              <TableRow>
                <TableCell sx={style.tableCell}>
                  <Typography variant="valuePlaceholder">{t('common:notSpecified')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!readOnly && (
        <Box sx={style.buttonBox}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRounded />}
            onClick={() => emailAddresses.append(new EmailAddressEntity())}
          >
            {t('common:add')}
          </Button>
        </Box>
      )}
    </>
  );
};
